import { forwardRef, useCallback, useState } from 'react'
import { Avatar, Button } from '@platform/ui-kit'
import classnames from 'classnames'
import { m, LazyMotion, domAnimation, useAnimation } from 'framer-motion'

import ClaraAvatar from '../../../assets/pngs/clara-avatar.png'
import ViniAvatar from '../../../assets/pngs/vini-avatar.png'

import OlbySpaceGraphics from './graphics'
import styles from './index.module.scss'

const OPACITY_ANIMATION = {
  start: { opacity: 0.01 },
  end: {
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TITLE_ANIMATION = {
  start: { opacity: 0.01, y: '50%' },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TITLE_CONTAINER_ANIMATION = {
  start: {
    y: '70%',
  },
  end: {
    y: '0%',
    transition: { type: 'spring', bounce: 0 },
  },
}

const SCROLL_INTO_VIEW = {
  start: { y: '100%', opacity: 0.01 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

export const ForTalentOlbySpace = forwardRef<HTMLElement>((_, ref) => {
  const headingControl = useAnimation()
  const descriptionControl = useAnimation()
  const headingContainerControl = useAnimation()
  const [wasTitleAnimationTriggered, setWasTitleAnimationTriggered] = useState(false)

  const onViewportEnter = useCallback(() => {
    if (wasTitleAnimationTriggered) {
      return
    }
    setWasTitleAnimationTriggered(true)

    headingControl.start('end').then(() => {
      descriptionControl.start('end')
      headingContainerControl.start('end')
    })
  }, [wasTitleAnimationTriggered, headingControl, descriptionControl, headingContainerControl])

  return (
    <LazyMotion features={domAnimation} strict>
      <m.section
        className={styles['section-wrapper']}
        ref={ref}
        viewport={{ once: true, amount: 0.3 }}
        initial="start"
        onViewportEnter={onViewportEnter}
      >
        <div className={styles['content']}>
          <m.div
            variants={TITLE_CONTAINER_ANIMATION}
            initial="start"
            animate={headingContainerControl}
          >
            <m.h2
              className={styles['title']}
              initial="start"
              variants={TITLE_ANIMATION}
              animate={headingControl}
            >
              Olby Space <br />
              <span className={styles['white']}>your career booster</span>
            </m.h2>
            <m.p
              className={styles['text']}
              animate={descriptionControl}
              variants={OPACITY_ANIMATION}
              initial="start"
            >
              Olby Space is a community made specifically{' '}
              <span className={styles['bold']}>for Olbers, by Olbers</span>.<br />
              Our purpose is to cultivate a diverse and inclusive environment where every member
              feels welcome and empowered to show their authentic selves.
            </m.p>
          </m.div>
          <OlbySpaceGraphics />
          <m.p
            className={styles['text']}
            variants={SCROLL_INTO_VIEW}
            viewport={{ once: true }}
            initial="start"
            whileInView="end"
          >
            Whether you have a question to ask or a new playlist to share, Olby Space is the{' '}
            <span className={styles['bold']}>perfect place to embrace your journey.</span>
          </m.p>
          <div className={styles['chat-wrapper']}>
            <m.div
              className={classnames(styles['message'], styles['first-message'])}
              variants={SCROLL_INTO_VIEW}
              viewport={{ once: true }}
              initial="start"
              whileInView="end"
            >
              <Avatar image={ClaraAvatar} name="Clara" size="xxl" className={styles['lg-avatar']} />
              <Avatar image={ClaraAvatar} name="Clara" size="xl" className={styles['md-avatar']} />
              <p className={styles['speech-bubble']}>
                Our ultimate goal is to help you thrive, and our community is a essential part of
                our strategy to make that happen.
              </p>
            </m.div>
            <m.div
              className={classnames(styles['message'], styles['second-message'])}
              variants={SCROLL_INTO_VIEW}
              viewport={{ once: true }}
              initial="start"
              whileInView="end"
            >
              <p className={styles['speech-bubble']}>
                By participating in Olby Space, you'll have the opportunity to both receive and
                provide support to grow and boost your career at the same time you see others
                growing.
              </p>
              <Avatar
                image={ViniAvatar}
                name="Vinicius"
                size="xxl"
                className={styles['lg-avatar']}
              />
              <Avatar
                image={ViniAvatar}
                name="Vinicius"
                size="xl"
                className={styles['md-avatar']}
              />
            </m.div>
          </div>
          <m.p
            className={styles['closing-statement']}
            variants={SCROLL_INTO_VIEW}
            viewport={{ once: true }}
            initial="start"
            whileInView="end"
          >
            You're just a few clicks away from a whole new universe.
          </m.p>
          <Button size="large" variant="secondary" href="/talent">
            Join Olby
          </Button>
        </div>
      </m.section>
    </LazyMotion>
  )
})
